import { useIncentives } from './incentives/useIncentives'
import { getApiClient } from '~/utils/api'
import { useAuthState } from '~/composables/useAuthState'
import { normalizeBadgeLocale, validCreatorBadges } from './contracts.mjs'
import type { CreatorProfile, CreatorRewards, CreatorBadges } from './types'

export class CreatorApiError extends Error {
  constructor(public code: number, public errorCode = '') { super('Creator request failed') }
}

/** Profile and billing remain independent; legacy award reads require a verified cohort. */
export function useCreator() {
  const { user, isAuthenticated, isAuthReady, fetchAndStoreToken } = useAuthState()
  const { session } = useSession()
  const { locale } = useI18n()
  const incentives = useIncentives()
  const language = computed(() => normalizeBadgeLocale(locale.value))
  const identity = computed(() => isAuthReady.value && isAuthenticated.value && user.value?.id && session.value?.id ? `${user.value.id}:${session.value.id}` : '')
  const state = useState('mm-creator', () => ({ owner: '', profile: null as CreatorProfile | null, rewards: null as CreatorRewards | null, badges: null as CreatorBadges | null, badgeLanguage: '', badgeRequestedLanguage: '', revisions: {} as Record<string, number>, loading: [] as string[], errors: [] as string[] }))
  // The unified UI never calls legacy award-on-read endpoints.
  const legacyRewardsAllowed = computed(() => false)
  const v2RewardsAvailable = computed(() => !!identity.value && incentives.state.identity === identity.value && !!incentives.state.subject && incentives.state.rewardEngine === 'v2')
  async function ensureRewardEngine(force = false) {
    if (!identity.value || !force && incentives.state.resources['incentives:overview']) return
    await incentives.load('incentives:overview', 'overview', {}, { force })
  }
  const profile = computed(() => state.value.owner === identity.value ? state.value.profile : null)
  const rewards = computed(() => legacyRewardsAllowed.value && state.value.owner === identity.value ? state.value.rewards : null)
  const badges = computed(() => legacyRewardsAllowed.value && state.value.owner === identity.value && state.value.badgeLanguage === language.value ? state.value.badges : null)
  async function request<T>(path: string, body: Record<string, unknown> | FormData = {}): Promise<T> {
    const legacyAward = ['creator/rewards', 'creator/badges', 'creator/badges/seen'].includes(path)
    const allowed = () => !legacyAward || legacyRewardsAllowed.value
    if (!allowed()) throw new CreatorApiError(409, 'reward_engine_unconfirmed')
    const owner = identity.value, snapshot = state.value
    if (!owner) throw new CreatorApiError(401)
    const token = await fetchAndStoreToken()
    if (!token || !allowed() || identity.value !== owner || state.value !== snapshot) throw new CreatorApiError(401)
    const response = await getApiClient().post(`music/v1/${path}`, body, {
      headers: { 'Content-Type': body instanceof FormData ? undefined : 'application/json', Authorization: `Bearer ${token}` },
      musicmintInlineError: true, validateStatus: () => true,
    })
    if (!allowed() || identity.value !== owner || state.value !== snapshot) throw new CreatorApiError(401)
    const envelope = response.data
    if (response.status < 200 || response.status >= 300 || envelope?.code !== 200) throw new CreatorApiError(envelope?.code || response.status, envelope?.data?.error_code || '')
    return envelope.data as T
  }
  async function load(resource: 'profile' | 'rewards' | 'badges', force = false) {
    const owner = identity.value, lang = language.value, snapshot = state.value
    if (resource !== 'profile' && !legacyRewardsAllowed.value) return
    if (!owner || snapshot.owner !== owner || !force && (snapshot.loading.includes(resource) || snapshot[resource] && (resource !== 'badges' || snapshot.badgeLanguage === lang))) return
    const revision = snapshot.revisions[resource] = (snapshot.revisions[resource] || 0) + 1
    if (resource === 'badges') snapshot.badgeRequestedLanguage = lang
    if (!snapshot.loading.includes(resource)) snapshot.loading.push(resource)
    snapshot.errors = snapshot.errors.filter(key => key !== resource)
    const current = () => (resource === 'profile' || legacyRewardsAllowed.value) && state.value === snapshot && identity.value === owner && snapshot.revisions[resource] === revision && (resource !== 'badges' || language.value === lang)
    try {
      const data = await request<any>(`creator/${resource === 'profile' ? 'profile/get' : resource}`, resource === 'badges' ? { lang } : {})
      if (resource === 'badges' && !validCreatorBadges(data)) throw new CreatorApiError(500)
      if (current()) { snapshot[resource] = data; if (resource === 'badges') snapshot.badgeLanguage = lang }
    } catch {
      if (current()) snapshot.errors.push(resource)
    } finally {
      if (current()) snapshot.loading = snapshot.loading.filter(key => key !== resource)
    }
  }
  async function updateProfile(body: { display_name?: string; avatar_upload_id?: string }) {
    const owner = identity.value, snapshot = state.value
    const data = await request<CreatorProfile>('creator/profile/update', body)
    if (identity.value !== owner || state.value !== snapshot) throw new CreatorApiError(401)
    snapshot.revisions.profile = (snapshot.revisions.profile || 0) + 1
    snapshot.loading = snapshot.loading.filter(key => key !== 'profile')
    snapshot.errors = snapshot.errors.filter(key => key !== 'profile')
    state.value.profile = { ...data, stats: profile.value?.stats }
    return data
  }
  async function confirmProfile() {
    const owner = identity.value, snapshot = state.value
    const data = await request<CreatorProfile>('creator/profile/confirm')
    if (identity.value !== owner || state.value !== snapshot) throw new CreatorApiError(401)
    snapshot.revisions.profile = (snapshot.revisions.profile || 0) + 1
    snapshot.loading = snapshot.loading.filter(key => key !== 'profile')
    snapshot.errors = snapshot.errors.filter(key => key !== 'profile')
    state.value.profile = { ...data, stats: profile.value?.stats }
  }
  async function markSeen(keys: string[]) {
    if (!legacyRewardsAllowed.value) throw new CreatorApiError(409, 'reward_engine_unconfirmed')
    const owner = identity.value, lang = language.value, snapshot = state.value
    const revision = snapshot.revisions.badges = (snapshot.revisions.badges || 0) + 1
    snapshot.loading = snapshot.loading.filter(key => key !== 'badges')
    const data = await request<CreatorBadges>('creator/badges/seen', { badge_keys: keys.slice(0, 100), lang })
    if (!validCreatorBadges(data)) throw new CreatorApiError(500)
    if (identity.value === owner && state.value === snapshot && language.value === lang && snapshot.revisions.badges === revision) {
      snapshot.badges = data; snapshot.badgeLanguage = lang
    }
  }
  watch([identity, language, legacyRewardsAllowed], ([owner, lang, legacy]) => {
    if (!import.meta.client) return
    if (state.value.owner !== owner) state.value = { owner, profile: null, rewards: null, badges: null, badgeLanguage: '', badgeRequestedLanguage: '', revisions: {}, loading: [], errors: [] }
    if (!legacy) {
      state.value.rewards = null; state.value.badges = null; state.value.badgeLanguage = ''; state.value.badgeRequestedLanguage = ''
      for (const key of ['rewards', 'badges']) state.value.revisions[key] = (state.value.revisions[key] || 0) + 1
      state.value.loading = state.value.loading.filter(key => key === 'profile')
      state.value.errors = state.value.errors.filter(key => key === 'profile')
    }
    if (owner) {
      void load('profile'); void ensureRewardEngine()
      if (!legacy) return
      void load('rewards')
      if (state.value.badgeRequestedLanguage !== lang || !state.value.badges && !state.value.loading.includes('badges')) void load('badges', state.value.badgeRequestedLanguage !== lang)
    }
  }, { immediate: import.meta.client, flush: 'sync' })
  return { identity, state, legacyRewardsAllowed, v2RewardsAvailable, ensureRewardEngine, profile, rewards, badges, request, load, updateProfile, confirmProfile, markSeen }
}
